import { clsx } from "clsx";
import { forwardRef, Fragment, type PropsWithChildren, type ReactElement, useRef } from "react";
import {
  type AriaTabListProps,
  FocusScope,
  mergeProps,
  OverlayContainer,
  useDialog,
  usePreventScroll,
} from "react-aria";

import { useDOMRef, useScroll } from "../../hooks.ts";
import { useTabList } from "../../hooks/useTabList.tsx";
import type {
  AllTabDialogProps,
  CommonDialogProps,
  DialogProps,
  TabDialogProps,
  TabPopoverProps,
} from "../../types/dialogs.d.ts";
import { HeaderBar, type HeaderBarProps } from "../HeaderBar/HeaderBar.tsx";
import { TabListBar } from "../Tabs/TabListBar.tsx";
import { TabPanel } from "../Tabs/TabPanel.tsx";
import { DialogButtonsWithErrorCallout } from "./DialogButtonsWithErrorCallout.tsx";
import style from "./TabDialog.module.css";
import { useDialogHeaderActionsWithClose } from "./useDialogHeaderActionsWithClose.tsx";

type TabDialogComponentProps = AllTabDialogProps & { domTitleProps: HeaderBarProps["domTitleProps"] };

export const TabDialogComponent = forwardRef<HTMLDivElement, TabDialogComponentProps>(function DialogComponent(
  props: TabDialogComponentProps,
  ref,
) {
  const {
    ref: tabRef,
    state,
    tabListProps,
  } = useTabList<HTMLDivElement>(props as PropsWithChildren<AriaTabListProps<ReactElement>>);
  const actions = useDialogHeaderActionsWithClose(props as CommonDialogProps);

  const scrollRef = useRef<HTMLDivElement>(null);
  useScroll(scrollRef);

  return (
    <div
      ref={ref}
      {...props.domProps}
      className={clsx(
        style.dialog,
        {
          [style.centered]: !!(props as DialogProps).underlayProps,
          [style.fullscreen]: props.fullscreen,
        },
        props.className,
      )}
      style={{ ...(props.domProps?.style || {}), ...props.style, width: props.width }}
      data-testid={"dialog"}
    >
      <div className={style.rounded_container}>
        <HeaderBar {...props} domTitleProps={props.domTitleProps} actions={actions} />
        <TabListBar state={state} tabListProps={tabListProps} ref={tabRef} className={style.dialog_tab} />
        {props.keepAlive ? (
          <TabPanel state={state} className={style.panel} keepAlive>
            {(children) => children.map((child) => <Fragment key={child.key}>{child.children}</Fragment>)}
          </TabPanel>
        ) : (
          <TabPanel state={state} className={style.panel} key={state.selectedKey}>
            {({ children, key }) => <Fragment key={key}>{children}</Fragment>}
          </TabPanel>
        )}

        {props.mainButton != undefined && (
          <DialogButtonsWithErrorCallout {...props} className={clsx(props.className, style.footer_bar)} />
        )}
      </div>
      {(props as TabPopoverProps).arrowProps != undefined && (
        <div className={style.arrow} {...(props as TabPopoverProps).arrowProps} />
      )}
    </div>
  );
});

/**
 * @deprecated
 */
export const TabDialog = forwardRef<HTMLDivElement, AllTabDialogProps>(function InfoTabDialog(props, ref) {
  const dialogRef = useDOMRef(ref);

  usePreventScroll();
  const { dialogProps, titleProps } = useDialog({ role: "dialog", id: props.id }, dialogRef);

  return (
    <OverlayContainer
      {...((props as TabDialogProps).underlayProps ?? {})}
      className={(props as TabDialogProps).underlayProps ? style.overlay : undefined}
    >
      <FocusScope contain restoreFocus autoFocus>
        <TabDialogComponent
          {...props}
          domProps={mergeProps(props.domProps, dialogProps)}
          domTitleProps={titleProps}
          ref={dialogRef}
        />
      </FocusScope>
    </OverlayContainer>
  );
});
